// contexts/AuthContext.tsx
'use client'

import { createContext, useContext, useState, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import LoginModal from '@/components/modals/login-modal'
import { RootState } from 'features/store'
import { isUserAvailable } from 'helpers/authUser'
import { usePersistBootstrapped } from '@/hook/usePersistBootstrapped'


export type AuthModalContext = {
  name: string
  userType?: string
}

type AuthContextType = {
  isAuthenticated: boolean
  showAuthModal: (redirectAfterLogin?: string, context?: AuthModalContext) => void
  hideAuthModal: () => void
  checkAuth: (context?: AuthModalContext) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [redirectAfterLogin, setRedirectAfterLogin] = useState<string>('/')
  const [authContext, setAuthContext] = useState<AuthModalContext | undefined>(undefined)
  const { user } = useSelector((state: RootState) => state.culineerAuth)
  const bootstrapped = usePersistBootstrapped()
  // Only consider authenticated if persist has completed rehydration and user is available
  const isAuthenticated = bootstrapped && isUserAvailable(user)

  const showAuthModal = (redirect?: string, context?: AuthModalContext) => {
    if (redirect) setRedirectAfterLogin(redirect)
    if (context) setAuthContext(context)
    setAuthModalOpen(true)
  }

  const hideAuthModal = () => {
    setAuthModalOpen(false)
    setAuthContext(undefined)
  }

  const checkAuth = (context?: AuthModalContext): boolean => {
    if (!isAuthenticated) {
      showAuthModal(window.location.pathname, context)
      return false
    }
    return true
  }

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      showAuthModal,
      hideAuthModal,
      checkAuth
    }}>
      {children}
      <LoginModal
        isOpen={authModalOpen}
        onClose={hideAuthModal}
        redirectPath={redirectAfterLogin}
        context={authContext}
      />
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}